import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import { getSoldProducts, getCategories, getCustomers, addSalePayment, getSaleItems, createReturn } from '../services/api';
import { debounce } from 'lodash';
import { showSuccess, showError, showWarning, showConfirm } from '../services/swal';
import { printInvoice } from '../services/printService';

const SoldProducts = () => {
    const [soldProducts, setSoldProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);

    // Filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('');

    // Data for filters
    const [categories, setCategories] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [filtersLoading, setFiltersLoading] = useState(true);
    
    // Statuses based on backend Sale model (paid, partial, pending)
    const invoiceStatuses = [
        { label: 'Fully Paid', value: 'paid' },
        { label: 'Settled', value: 'completed' },
        { label: 'Partial', value: 'partial' },
        { label: 'Pending', value: 'pending' },
        { label: 'Cancelled', value: 'cancelled' }
    ];

    // Payment Modal State
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedSale, setSelectedSale] = useState(null);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [paymentLoading, setPaymentLoading] = useState(false);

    // Return Modal State
    const [showReturnModal, setShowReturnModal] = useState(false);
    const [returnQty, setReturnQty] = useState(1);
    const [returnReason, setReturnReason] = useState('Damaged');
    const [customReason, setCustomReason] = useState('');

    const fetchFilterData = async () => {
        setFiltersLoading(true);
        try {
            const [catResponse, custResponse] = await Promise.all([
                getCategories({ all: true }),
                getCustomers({ all: true })
            ]);
            setCategories(catResponse.data || []);
            setCustomers(custResponse.data.data || []);
        } catch (err) {
            console.error("Failed to fetch filter data", err);
            setError("Could not load filter options.");
        } finally {
            setFiltersLoading(false);
        }
    };

    const fetchSoldProducts = useCallback(async (page = 1) => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                page,
                search: searchTerm,
                category_id: selectedCategory,
                customer_id: selectedCustomer,
                invoice_status: selectedStatus,
            };
            const response = await getSoldProducts(params);
            setSoldProducts(response.data.data);
            setCurrentPage(response.data.current_page);
            setLastPage(response.data.last_page);
        } catch (err) {
            setError('Failed to fetch sold products.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [searchTerm, selectedCategory, selectedCustomer, selectedStatus]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedFetch = useCallback(debounce((page) => fetchSoldProducts(page), 500), [fetchSoldProducts]);

    useEffect(() => {
        fetchFilterData();
    }, []);

    useEffect(() => {
        debouncedFetch(1);
        return () => debouncedFetch.cancel();
    }, [searchTerm, selectedCategory, selectedCustomer, selectedStatus, debouncedFetch]);

    const handlePageChange = (page) => {
        fetchSoldProducts(page);
    };

    const clearFilters = () => {
        setSearchTerm('');
        setSelectedCategory('');
        setSelectedCustomer('');
        setSelectedStatus('');
        setCurrentPage(1);
    };

    const openPaymentModal = (item) => {
        setSelectedSale(item);
        setPaymentAmount('');
        setShowPaymentModal(true);
    };

    const handleAddPayment = async () => {
        if (!selectedSale || !paymentAmount) return;
        setPaymentLoading(true);
        try {
            await addSalePayment(selectedSale.sale_id, { amount: paymentAmount });
            setShowPaymentModal(false);
            fetchSoldProducts(currentPage);
            showSuccess('Payment Recorded', 'Transaction successfully updated.');
        } catch (err) {
            showError('Operation Failed', err.response?.data?.message || err.message);
        } finally {
            setPaymentLoading(false);
        }
    };

    const handlePrint = async (item) => {
        try {
            setPaymentLoading(true);
            const res = await getSaleItems(item.sale_id);
            const saleItems = res.data.items || res.data;
            printInvoice({
                invoice_number: item.invoice_number || `INV-${item.sale_id}`,
                customer_name: item.customer,
                items: saleItems,
                final_amount: item.sale_total || item.total_price,
                paid_amount: item.paid_amount || item.total_price,
                due_amount: item.due_amount || 0,
                date: item.sale_date
            });
        } catch (err) {
            showError('Print Error', 'Failed to retrieve invoice details.');
        } finally {
            setPaymentLoading(false);
        }
    };

    const handleReturn = (item) => {
        setSelectedSale(item);
        setReturnQty(1);
        setReturnReason('Damaged');
        setCustomReason('');
        setShowReturnModal(true);
    };

    const submitReturn = async () => {
        if (returnQty > selectedSale.quantity) {
            showWarning('Invalid Quantity', `Cannot return more than ${selectedSale.quantity} units.`);
            return;
        }

        const finalReason = returnReason === 'Other' ? customReason : returnReason;
        if (returnReason === 'Other' && !customReason.trim()) {
            showWarning('Reason Required', 'Please provide a reason for the return.');
            return;
        }

        setPaymentLoading(true);
        try {
            await createReturn({
                sale_id: selectedSale.sale_id,
                items: [{
                    product_id: selectedSale.product_id, // Now correctly provided by fixed backend
                    quantity: returnQty
                }],
                reason: finalReason
            });
            setShowReturnModal(false);
            fetchSoldProducts(currentPage);
            showSuccess('Return Successful', 'Items have been returned to stock.');
        } catch (err) {
            showError('Return Failed', err.response?.data?.message || err.message);
        } finally {
            setPaymentLoading(false);
        }
    };

    // Calculate quick stats from current page
    const totalQty = soldProducts.reduce((acc, curr) => acc + (curr.quantity || 0), 0);
    const totalRev = soldProducts.reduce((acc, curr) => acc + parseFloat(curr.total_price || 0), 0);

    return (
        <Layout title="Sold Products Audit">
            {/* Quick Intelligence Dashboard */}
            <div className="row g-4 mb-4">
                <div className="col-12 col-md-6 col-xl-3">
                    <div className="report-stat-card border-0 shadow-sm animate__animated animate__fadeIn">
                        <div className="icon-badge bg-primary bg-opacity-10 text-primary mb-3">
                            <i className="bi bi-box-seam fs-4"></i>
                        </div>
                        <small className="text-muted d-block fw-bold mb-1">Units Distributed</small>
                        <h3 className="fw-bold mb-0">{totalQty} <span className="small text-muted fw-normal">Items</span></h3>
                    </div>
                </div>
                <div className="col-12 col-md-6 col-xl-3">
                    <div className="report-stat-card border-0 shadow-sm animate__animated animate__fadeIn" style={{ animationDelay: '0.1s' }}>
                        <div className="icon-badge bg-success bg-opacity-10 text-success mb-3">
                            <i className="bi bi-currency-dollar fs-4"></i>
                        </div>
                        <small className="text-muted d-block fw-bold mb-1">Total Sales Value</small>
                        <h3 className="fw-bold mb-0">Rs. {totalRev.toLocaleString()}</h3>
                    </div>
                </div>
                <div className="col-12 col-md-6 col-xl-3">
                    <div className="report-stat-card border-0 shadow-sm animate__animated animate__fadeIn" style={{ animationDelay: '0.2s' }}>
                        <div className="icon-badge bg-warning bg-opacity-10 text-warning mb-3">
                            <i className="bi bi-clock-history fs-4"></i>
                        </div>
                        <small className="text-muted d-block fw-bold mb-1">Active Receivables</small>
                        <h3 className="fw-bold mb-0">Rs. {soldProducts.reduce((acc, curr) => acc + parseFloat(curr.due_amount || 0), 0).toLocaleString()}</h3>
                    </div>
                </div>
                <div className="col-12 col-md-6 col-xl-3">
                    <div className="report-stat-card border-0 shadow-sm animate__animated animate__fadeIn" style={{ animationDelay: '0.3s' }}>
                        <div className="icon-badge bg-info bg-opacity-10 text-info mb-3">
                            <i className="bi bi-people-fill fs-4"></i>
                        </div>
                        <small className="text-muted d-block fw-bold mb-1">Unique Clients</small>
                        <h3 className="fw-bold mb-0">{[...new Set(soldProducts.map(i => i.customer))].length} <span className="small text-muted fw-normal">Accounts</span></h3>
                    </div>
                </div>
            </div>

            {/* Advanced Discovery Bar */}
            <div className="report-discovery-bar shadow-sm border-0 mb-4 animate__animated animate__fadeInUp">
                <div className="row g-3 align-items-center">
                    <div className="col-12 col-xl-4">
                        <div className="search-wrapper position-relative">
                            <i className="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted"></i>
                            <input
                                type="text"
                                className="form-control pos-search-input"
                                placeholder="Audit products, SKUs or categories..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{ height: '52px', borderRadius: '14px', paddingLeft: '3.2rem', border: '2px solid var(--border-color)', background: 'var(--bs-body-bg)' }}
                            />
                        </div>
                    </div>
                    <div className="col-6 col-md-4 col-xl-2">
                        <select className="form-select border-2" style={{ height: '52px', borderRadius: '14px' }} value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
                            <option value="">All Categories</option>
                            {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div className="col-6 col-md-4 col-xl-2">
                        <select className="form-select border-2" style={{ height: '52px', borderRadius: '14px' }} value={selectedCustomer} onChange={(e) => setSelectedCustomer(e.target.value)}>
                            <option value="">All Customers</option>
                            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </div>
                    <div className="col-12 col-md-4 col-xl-2">
                        <select className="form-select border-2" style={{ height: '52px', borderRadius: '14px' }} value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                            <option value="">All Statuses</option>
                            {invoiceStatuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                    </div>
                    <div className="col-12 col-xl-2 d-flex gap-2">
                        <button className="btn btn-light border fw-bold w-100" onClick={clearFilters} style={{ height: '52px', borderRadius: '14px' }}>
                            <i className="bi bi-arrow-counterclockwise me-2"></i>Reset
                        </button>
                    </div>
                </div>
            </div>

            {/* Audit Grid */}
            <div className="report-table shadow-sm border-0 animate__animated animate__fadeIn">
                <div className="table-responsive">
                    <table className="table align-middle mb-0">
                        <thead>
                            <tr>
                                <th className="ps-4">Product Detail</th>
                                <th>Invoice #</th>
                                <th>Client / Entity</th>
                                <th>Transaction Status</th>
                                <th className="text-center">Units</th>
                                <th className="text-end">Value (Rs.)</th>
                                <th className="text-end pe-4">Management</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="7" className="text-center py-5">
                                        <div className="spinner-border text-primary"></div>
                                    </td>
                                </tr>
                            ) : soldProducts.length === 0 ? (
                                <tr>
                                    <td colSpan="7" className="text-center py-5 text-muted">No records found matching your filters.</td>
                                </tr>
                            ) : (
                                soldProducts.map(item => (
                                    <tr key={item.id} className="hover-light transition-all">
                                        <td className="ps-4 py-3">
                                            <div className="fw-bold text-dark">{item.product_name}</div>
                                            <span className="badge bg-light text-muted border-0 small">{item.category}</span>
                                        </td>
                                        <td>
                                            <div className="fw-bold text-primary">{item.invoice_number}</div>
                                            <small className="text-muted">Source Audit</small>
                                        </td>
                                        <td>
                                            <div className="fw-medium">{item.customer}</div>
                                            <small className="text-muted">{new Date(item.sale_date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</small>
                                        </td>
                                        <td>
                                            <div className={`badge ${item.returned_quantity >= item.quantity ? 'bg-danger' : item.returned_quantity > 0 ? 'bg-warning text-dark' : (item.status === 'completed' || item.status === 'paid' ? 'bg-success' : 'bg-warning')} bg-opacity-10 ${item.returned_quantity >= item.quantity ? 'text-danger' : item.returned_quantity > 0 ? 'text-warning' : (item.status === 'completed' || item.status === 'paid' ? 'text-success' : 'text-warning')} border-0 px-3 py-2 rounded-pill small fw-bold`}>
                                                {item.returned_quantity >= item.quantity ? 'FULLY RETURNED' : item.returned_quantity > 0 ? `PARTIAL (${item.returned_quantity} Ret)` : (item.status || 'PAID').toUpperCase()}
                                            </div>
                                            {item.returned_quantity > 0 && <div className="mt-1 small text-muted text-center"><i className="bi bi-arrow-return-left"></i> Reversed</div>}
                                        </td>
                                        <td className="text-center fw-bold text-dark">{item.quantity}</td>
                                        <td className="text-end fw-bold text-primary">Rs. {parseFloat(item.total_price).toLocaleString()}</td>
                                        <td className="text-end pe-4">
                                            <div className="d-flex justify-content-end gap-2 no-print">
                                                <button className="btn btn-sm btn-light border rounded-pill px-3" onClick={() => handlePrint(item)} title="Print Proof">
                                                    <i className="bi bi-printer"></i>
                                                </button>
                                                <button className="btn btn-sm btn-outline-danger rounded-pill px-3" onClick={() => handleReturn(item)} title="Return Item">
                                                    <i className="bi bi-arrow-return-left me-1"></i>Return
                                                </button>
                                                <button className="btn btn-sm btn-primary rounded-pill px-3" onClick={() => openPaymentModal(item)} disabled={item.status === 'completed' || item.status === 'paid' || item.status === 'cancelled'}>
                                                    <i className="bi bi-wallet2 me-1"></i> Pay
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {!loading && soldProducts.length > 0 && (
                    <div className="d-flex justify-content-between align-items-center p-4 border-top bg-light-subtle no-print">
                        <span className="text-muted small fw-medium">
                            Displaying record <span className="text-dark">{(currentPage-1)*15 + 1}</span> to <span className="text-dark">{Math.min(currentPage*15, (currentPage-1)*15 + soldProducts.length)}</span>
                        </span>
                        <Pagination currentPage={currentPage} lastPage={lastPage} onPageChange={handlePageChange} />
                    </div>
                )}
            </div>

            {/* Payment Settle Modal */}
            {showPaymentModal && (
                <div className="modal d-block animate__animated animate__fadeIn" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={(e) => e.target === e.currentTarget && setShowPaymentModal(false)}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '24px' }}>
                            <div className="modal-header border-0 pt-4 px-4">
                                <h5 className="modal-title fw-bold">Settle Transaction</h5>
                                <button className="btn-close" onClick={() => setShowPaymentModal(false)}></button>
                            </div>
                            <div className="modal-body px-4 pb-4 text-center">
                                <div className="icon-badge bg-primary bg-opacity-10 text-primary mx-auto mb-3" style={{ width: '80px', height: '80px', fontSize: '2rem' }}>
                                    <i className="bi bi-cash-coin"></i>
                                </div>
                                <h4 className="fw-bold mb-1">{selectedSale?.product_name}</h4>
                                <p className="text-muted mb-4">Invoice #{selectedSale?.invoice_number || selectedSale?.sale_id}</p>
                                
                                <div className="p-3 bg-light rounded-4 mb-4 text-start">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <span className="text-muted small">Outstanding Balance</span>
                                        <span className="fw-bold text-danger">Rs. {parseFloat(selectedSale?.due_amount || 0).toLocaleString()}</span>
                                    </div>
                                    <div className="input-group input-group-lg shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                                        <span className="input-group-text bg-white border-end-0 text-muted">Rs.</span>
                                        <input
                                            type="number"
                                            className="form-control border-start-0 ps-0"
                                            value={paymentAmount}
                                            onChange={e => setPaymentAmount(e.target.value)}
                                            placeholder="Enter amount..."
                                            style={{ boxShadow: 'none', height: '60px' }}
                                        />
                                    </div>
                                </div>
                                <div className="row g-2">
                                    <div className="col-6">
                                        <button className="btn btn-light border w-100 fw-bold py-3" onClick={() => setShowPaymentModal(false)} style={{ borderRadius: '16px' }}>Later</button>
                                    </div>
                                    <div className="col-6">
                                        <button className="btn btn-primary w-100 fw-bold shadow-sm py-3" onClick={handleAddPayment} disabled={paymentLoading} style={{ borderRadius: '16px' }}>
                                            {paymentLoading ? <span className="spinner-border spinner-border-sm"></span> : 'Settle Now'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Return Modal */}
            {showReturnModal && (
                <div className="modal d-block animate__animated animate__fadeIn" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={(e) => e.target === e.currentTarget && setShowReturnModal(false)}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '24px' }}>
                            <div className="modal-header border-0 pt-4 px-4">
                                <h5 className="modal-title fw-bold">Process Item Return</h5>
                                <button className="btn-close" onClick={() => setShowReturnModal(false)}></button>
                            </div>
                            <div className="modal-body px-4 pb-4">
                                <div className="p-3 bg-danger bg-opacity-10 rounded-4 mb-4 text-center">
                                    <i className="bi bi-arrow-return-left text-danger fs-1"></i>
                                    <h5 className="fw-bold text-danger mt-2">Return {selectedSale?.product_name}</h5>
                                    <p className="small text-muted mb-0">This will restore stock and update customer ledger.</p>
                                </div>
                                
                                <div className="mb-3">
                                    <label className="form-label small fw-bold text-muted text-uppercase">Quantity to Return</label>
                                    <div className="input-group input-group-lg shadow-sm" style={{ borderRadius: '16px', overflow: 'hidden' }}>
                                        <button className="btn btn-light border-end" onClick={() => setReturnQty(Math.max(1, returnQty - 1))}><i className="bi bi-dash-lg"></i></button>
                                        <input type="number" className="form-control text-center fw-bold" value={returnQty} readOnly />
                                        <button className="btn btn-light border-start" onClick={() => setReturnQty(Math.min(selectedSale.quantity, returnQty + 1))}><i className="bi bi-plus-lg"></i></button>
                                    </div>
                                    <small className="text-muted mt-2 d-block text-center">Max returnable: {selectedSale.quantity} units</small>
                                </div>

                                <div className="mb-4">
                                    <label className="form-label small fw-bold text-muted text-uppercase">Reason for Return</label>
                                    <select className="form-select form-select-lg border-2 mb-3" style={{ borderRadius: '16px', height: '56px' }} value={returnReason} onChange={e => setReturnReason(e.target.value)}>
                                        <option value="Damaged">Damaged / Defective</option>
                                        <option value="Wrong Item">Wrong Item Sent</option>
                                        <option value="Expired">Expired Stock</option>
                                        <option value="Customer Dissatisfied">Customer Dissatisfied</option>
                                        <option value="Other">Other (Specify below)</option>
                                    </select>
                                    
                                    {returnReason === 'Other' && (
                                        <div className="animate__animated animate__fadeIn">
                                            <input 
                                                type="text" 
                                                className="form-control form-control-lg border-2" 
                                                placeholder="Please specify return reason..." 
                                                value={customReason} 
                                                onChange={e => setCustomReason(e.target.value)}
                                                style={{ borderRadius: '16px', height: '56px' }}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="row g-2">
                                    <div className="col-6">
                                        <button className="btn btn-light border w-100 fw-bold py-3" onClick={() => setShowReturnModal(false)} style={{ borderRadius: '16px' }}>Cancel</button>
                                    </div>
                                    <div className="col-6">
                                        <button className="btn btn-danger w-100 fw-bold shadow-sm py-3" onClick={submitReturn} disabled={paymentLoading} style={{ borderRadius: '16px' }}>
                                            {paymentLoading ? <span className="spinner-border spinner-border-sm"></span> : 'Process Return'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            <style>{`
                .report-stat-card {
                    background: var(--card-bg);
                    border-radius: 20px;
                    padding: 24px;
                    border: 1px solid var(--border-color);
                    transition: all 0.3s ease;
                }
                .report-discovery-bar {
                    background: var(--card-bg);
                    border-radius: 24px;
                    padding: 24px;
                    border: 1px solid var(--border-color);
                }
                .report-table {
                    background: var(--card-bg);
                    border-radius: 24px;
                    overflow: hidden;
                    border: 1px solid var(--border-color);
                }
                .icon-badge {
                    width: 45px;
                    height: 45px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 1.25rem;
                }
                
                /* Dark Mode Overrides */
                [data-bs-theme="dark"] .bg-white,
                [data-bs-theme="dark"] .bg-light,
                [data-bs-theme="dark"] .bg-light-subtle {
                    background-color: var(--card-bg) !important;
                    color: var(--text-color) !important;
                }
                [data-bs-theme="dark"] .modal-content,
                [data-bs-theme="dark"] .modal-body .bg-white {
                    background-color: var(--card-bg) !important;
                }
                [data-bs-theme="dark"] .modal-body .bg-light,
                [data-bs-theme="dark"] .modal-footer.bg-light {
                    background-color: rgba(255,255,255,0.02) !important;
                }
                [data-bs-theme="dark"] .pos-search-input,
                [data-bs-theme="dark"] .form-control,
                [data-bs-theme="dark"] .form-select {
                    background-color: rgba(255,255,255,0.03) !important;
                    border-color: var(--border-color) !important;
                    color: var(--text-color) !important;
                }
                [data-bs-theme="dark"] .table {
                    --bs-table-bg: transparent;
                    --bs-table-color: var(--text-color);
                    --bs-table-border-color: var(--border-color);
                }
                [data-bs-theme="dark"] .hover-light:hover {
                    background-color: rgba(255,255,255,0.03) !important;
                }
                [data-bs-theme="dark"] .text-dark {
                    color: var(--text-color) !important;
                }
                [data-bs-theme="dark"] .text-muted {
                    color: var(--text-muted-color) !important;
                }
                [data-bs-theme="dark"] .btn-light {
                    background-color: rgba(255,255,255,0.05) !important;
                    border-color: var(--border-color) !important;
                    color: var(--text-color) !important;
                }
            `}</style>
        </Layout>
    );
};

export default SoldProducts;
